import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector(state => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));

    const Icon = item.icon;
    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size='1.15rem' />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: customization.isOpen.findIndex(id => id === item?.id) > -1 ? 8 : 6,
                height: customization.isOpen.findIndex(id => id === item?.id) > -1 ? 8 : 6,
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />),
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = id => {
        dispatch({ type: MENU_OPEN, id });
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
    };

    // active menu item on page load
    useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex(id => id === item.id);
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
        // eslint-disable-next-line
    }, []);

    const isSelected = customization.isOpen.findIndex(id => id === item.id) > -1;

    return (
        <ListItemButton
            {...listItemProps}
            disabled={item.disabled}
            sx={{
                borderRadius: '12px',
                mb: 1,
                alignItems: 'center',
                backgroundColor: isSelected
                    ? 'rgba(255,255,255,0.25)'
                    : level > 1
                    ? 'transparent !important'
                    : 'transparent',
                py: level > 1 ? 0.75 : 1,
                pl: `${level * 16 + 8}px`,
                pr: 1.5,
                color: 'white',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': isSelected
                    ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 100%)',
                          borderRadius: '0 4px 4px 0',
                          boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                      }
                    : {},
                '&:hover': {
                    backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                    transform: 'translateX(6px)',
                    boxShadow: isSelected ? '0 6px 20px rgba(255,255,255,0.3)' : '0 4px 15px rgba(0,0,0,0.2)',
                },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                    fontWeight: 700,
                    boxShadow: '0 4px 15px rgba(255,255,255,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        transform: 'translateX(6px)',
                    },
                },
                '&.Mui-selected .MuiListItemIcon-root': {
                    color: '#FFD700',
                    transform: 'scale(1.15)',
                },
                '&.Mui-disabled': {
                    opacity: 0.5,
                },
            }}
            selected={isSelected}
            onClick={() => itemHandler(item.id)}
        >
            <ListItemIcon
                sx={{
                    my: 'auto',
                    minWidth: !item?.icon ? 18 : 40,
                    color: isSelected ? '#FFD700' : 'rgba(255,255,255,0.9)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'scale(1.15) rotate(5deg)',
                    },
                }}
            >
                {itemIcon}
            </ListItemIcon>
            <ListItemText
                primary={
                    <Typography
                        variant='body2'
                        sx={{
                            color: 'inherit',
                            fontWeight: isSelected ? 600 : 500,
                            fontSize: '0.8125rem',
                            letterSpacing: '0.2px',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        {item.title}
                    </Typography>
                }
                secondary={
                    item.caption && (
                        <Typography
                            variant='caption'
                            sx={{
                                ...theme.typography.subMenuCaption,
                                color: 'rgba(255,255,255,0.7)',
                                fontSize: '0.75rem',
                                mt: 0.5,
                            }}
                            display='block'
                            gutterBottom
                        >
                            {item.caption}
                        </Typography>
                    )
                }
            />
            {item.chip && (
                <Chip
                    color={item.chip.color}
                    variant={item.chip.variant}
                    size={item.chip.size}
                    label={item.chip.label}
                    avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                    sx={{
                        ml: 1,
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    }}
                />
            )}
        </ListItemButton>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
};

export default NavItem;
